import React, { useState } from 'react'
import { IFormProps } from '../../lib/types'
import { FormikProps, Formik } from 'formik'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import {
  Input,
  Button,
  Flex,
  SelectDropdown,
  PendingIcon,
  Anchor,
  CheckboxInput,
} from '../../lib/components'
import { COUNTRIES } from '../../lib/utils/message'
import { useBreakpoint } from '../../lib/utils/hooks'
import { XL } from '../../lib/utils/constants'
import { BasicDetailsProfile } from '../../lib/utils/onboarding'
import { ProfileGeneralReadonly } from './profile-general-readonly'

interface GeneralFormValues {
  name: string
  countryOfResidence: string
  email: string
  phoneNumber: string
  citizenship?: string
  countryCode?: string
  passportNumber?: string
  whatsappConsent: boolean
}

interface GeneralFormProps extends Omit<IFormProps, 'initialValues'> {
  initialValues: GeneralFormValues
  isReadOnly?: boolean
  basicDetailsProfile?: BasicDetailsProfile | null
}

const ProfileSupportNotice = () => (
  <div className='rm-details-container'>
    <div className='payment-warning-container-full mt-0'>
      <PendingIcon classes={['info-icon']} width='24' height='24' />
      <div>
        For any assistance, contact our support team at
        <Anchor
          variant='basic-anchor'
          classes={['ml-4', 'email-link']}
          href='mailto:support@credibila.com'
          text='support@credibila.com'
        />
        . You can also reach out to your dedicated Relationship Manager, Khushboo Agarwal, at
        <Anchor
          variant='basic-anchor'
          classes={['ml-4', 'email-link']}
          href='mailto:khushboo.agarwal@credibila.com'
          text='khushboo.agarwal@credibila.com'
        />{' '}
        for personalized support. Our working hours are 10 AM to 6 PM Dubai time (GST).
      </div>
    </div>
  </div>
)

const GeneralForm = ({
  initialValues,
  handleFormSubmit,
  validationSchema,
  isReadOnly = false,
  basicDetailsProfile = null,
}: GeneralFormProps) => {
  const { breakpoint } = useBreakpoint()
  const [isValidPhoneNumberValue, setIsValidPhoneNumberValue] = useState(true)

  const typedInitialValues: GeneralFormValues = {
    ...initialValues,
    name: initialValues.name || '',
    countryOfResidence: initialValues.countryOfResidence || '',
    email: initialValues.email || '',
    phoneNumber: initialValues.phoneNumber || '',
    citizenship: initialValues.citizenship || '',
    whatsappConsent: Boolean(initialValues.whatsappConsent),
  }

  if (isReadOnly && basicDetailsProfile) {
    return (
      <>
        <ProfileSupportNotice />
        <ProfileGeneralReadonly profile={basicDetailsProfile} />
      </>
    )
  }

  return (
    <Formik
      enableReinitialize
      initialValues={typedInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
    >
      {(formik) => {
        const {
          values,
          touched,
          errors,
          dirty,
          handleSubmit,
          setFieldValue,
        }: FormikProps<GeneralFormValues> = formik
        return (
          <>
            <ProfileSupportNotice />
            <form>
              <Flex variant={breakpoint && breakpoint > XL ? 'space-between' : 'column'}>
                <div className='general-input-container'>
                  <Input
                    labelText='Your name'
                    name='name'
                    placeholder='User Client'
                    type='text'
                    required
                    value={values.name}
                    handleChange={(e) => setFieldValue('name', e.target.value)}
                    dirty={dirty}
                    errors={{
                      name: !!errors && errors.name ? errors.name : null,
                    }}
                    touched={{ name: !!touched && !!touched.name }}
                    data-testid='profile-general-name-input'
                  />
                </div>
                <div
                  className={`general-input-container ${
                    breakpoint && breakpoint > XL ? '' : 'mt-36'
                  }`}
                >
                  <span className='general-input-label'> Country of residence </span>
                  <SelectDropdown
                    name='countryOfResidence'
                    id='countryOfResidenceId'
                    classes={['general-input-select']}
                    formId=''
                    list={COUNTRIES}
                    value={values.countryOfResidence}
                    handleChange={(val: string) => setFieldValue('countryOfResidence', val)}
                    dirty={dirty}
                    errors={{
                      countryOfResidence:
                        !!errors && errors.countryOfResidence ? errors.countryOfResidence : null,
                    }}
                    touched={{
                      countryOfResidence: !!touched && !!touched.countryOfResidence,
                    }}
                    dataTestId='profile-general-country-select'
                  />
                </div>
              </Flex>
              <Flex
                classes={['mt-32']}
                variant={breakpoint && breakpoint > XL ? 'space-between' : 'column'}
              >
                <div className='general-input-container'>
                  <Input
                    labelText='Email'
                    name='email'
                    placeholder='user@email.com'
                    type='email'
                    disabled
                    required
                    value={values.email}
                    data-testid='profile-general-email-input'
                  />
                </div>
                <div
                  className={`general-input-container ${
                    breakpoint && breakpoint > XL ? '' : 'mt-36'
                  }`}
                >
                  <span className='general-input-label'> Mobile phone </span>
                  <PhoneInput
                    international
                    country={initialValues.countryCode}
                    placeholder='Enter phone number'
                    value={values.phoneNumber}
                    onChange={(val: unknown) => {
                      if (val && isValidPhoneNumber(String(val))) {
                        setFieldValue('phoneNumber', String(val))
                        setIsValidPhoneNumberValue(true)
                      } else setIsValidPhoneNumberValue(false)
                    }}
                    data-testid='profile-general-phone-input'
                  />
                  {!isValidPhoneNumberValue && (
                    <span className='general-input-error'> Invalid Mobile Number </span>
                  )}

                  <div className='whatsapp-consent-container mt-16'>
                    <CheckboxInput
                      name='whatsappConsent'
                      checked={values.whatsappConsent}
                      value={values.whatsappConsent ? 'true' : 'false'}
                      handleChange={() => setFieldValue('whatsappConsent', !values.whatsappConsent)}
                      labelText='I consent to receive communications via WhatsApp'
                      disabled={typedInitialValues.whatsappConsent}
                      dataTestId='profile-general-whatsapp-consent'
                    />
                  </div>
                </div>
              </Flex>
              <Flex
                classes={['mt-32']}
                variant={breakpoint && breakpoint > XL ? 'space-between' : 'column'}
              >
                <div className='general-input-container'>
                  <span className='general-input-label'> Citizenship </span>
                  <SelectDropdown
                    name='citizenship'
                    id='citizenshipId'
                    classes={['general-input-select']}
                    formId=''
                    list={COUNTRIES}
                    value={values.citizenship ?? ''}
                    handleChange={(val: string) => setFieldValue('citizenship', val)}
                    dirty={dirty}
                    errors={{
                      citizenship: !!errors && errors.citizenship ? errors.citizenship : null,
                    }}
                    touched={{
                      citizenship: !!touched && !!touched.citizenship,
                    }}
                    dataTestId='profile-general-citizenship-select'
                  />
                </div>
              </Flex>
              <Button
                text='Save changes'
                classes={['auto-btn-width', 'button-top-margin']}
                variant='primary-btn'
                handleClick={handleSubmit}
              />
            </form>
          </>
        )
      }}
    </Formik>
  )
}

export { GeneralForm }
